import { Check, X } from 'lucide-react';
import { useI18n } from '../lib/i18n';

/**
 * EL ESTADO DE UN BUZÓN, CON LA FECHA PEGADA.
 *
 * Dos cosas que esta pieza existe para arreglar, y las dos son de la pantalla, no del servidor:
 *
 * 1. UN BADGE SIN FECHA AL LADO ES UN ENGAÑO. El 2026-08-07 las 38 casillas de producción decían
 *    "✓ conecta" con un chequeo del 5 de agosto: la pantalla mostraba un recuerdo de dos días
 *    atrás como si fuera el estado de ahora. Por eso el "cuándo" no es un detalle opcional acá —
 *    va SIEMPRE, y si el chequeo venció (`stale`) el verde se apaga a gris. Preferimos decir "no
 *    sabemos" antes que decir "anda" sin saberlo.
 *
 * 2. RECIBIR Y ENVIAR SON DOS COSAS. Se dibuja UNA por canal: Laura falla al recibir y Paula al
 *    enviar, y con un solo semáforo las dos fichas se veían idénticas — el caso de Paula
 *    directamente no aparecía en ninguna pantalla.
 */

export type EstadoCanal = {
  ok: boolean | null;
  checked_at: string | null;
  error: string | null;
  failing_since: string | null;
  stale: boolean;
};

/** "recién / hace 5 min / hace 3 h / hace 2 d" — la unidad más grande que todavía dice algo. */
export function useHace() {
  const { t } = useI18n();
  return (iso: string | null): string => {
    if (!iso) return t('mbh.never');
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (!Number.isFinite(min) || min < 2) return t('mbh.agoNow');
    if (min < 60) return t('mbh.agoMin', { n: String(min) });
    const h = Math.floor(min / 60);
    if (h < 24) return t('mbh.agoHour', { n: String(h) });
    return t('mbh.agoDay', { n: String(Math.floor(h / 24)) });
  };
}

/** Los tres colores posibles + el cuarto que faltaba: "esto está vencido, no lo sé". */
function tono(e: EstadoCanal) {
  if (e.ok === false) return { bg: 'rgba(185,28,28,.12)', fg: '#b91c1c', signo: <X size={12} strokeWidth={1.75} /> };
  if (e.ok === true && !e.stale) return { bg: 'rgba(22,163,74,.14)', fg: '#16a34a', signo: <Check size={12} strokeWidth={1.75} /> };
  // Verde vencido y "nunca se probó" se dibujan IGUAL a propósito: en los dos casos la respuesta
  // honesta es la misma, no sabemos si funciona ahora.
  return { bg: 'var(--surface-2)', fg: 'var(--muted)', signo: <>·</> };
}

export function MailboxChannelBadge(
  { canal, estado, compacto = false }:
  { canal: 'imap' | 'smtp'; estado: EstadoCanal; compacto?: boolean },
) {
  const { t } = useI18n();
  const hace = useHace();
  const { bg, fg, signo } = tono(estado);
  const nombre = t(canal === 'imap' ? 'mbh.receiving' : 'mbh.sending');

  const cuando = estado.checked_at == null ? t('mbh.untested')
    : estado.ok === false ? t('mbh.failingSince', { d: hace(estado.failing_since ?? estado.checked_at) })
      : estado.stale ? t('mbh.staleSince', { d: hace(estado.checked_at) })
        : t('mbh.checkedAgo', { d: hace(estado.checked_at) });

  // El error del servidor va en el title: es lo que distingue "contraseña mal" de "el servidor nos
  // está cortando", y son dos arreglos completamente distintos.
  const detalle = [nombre, cuando, estado.error].filter(Boolean).join(' — ');

  return (
    <span
      title={detalle}
      className="badge"
      style={{
        background: bg, color: fg, fontSize: compacto ? 9.5 : 10.5, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>{signo}{nombre}</span>
      {/* La fecha NUNCA es opcional: sin ella el color vuelve a ser una promesa sin respaldo. */}
      <span style={{ opacity: 0.75, fontWeight: 600 }}>{cuando}</span>
    </span>
  );
}

/** Las dos, que es como hay que mirarlas: juntas y por separado. */
export function MailboxChannelBadges(
  { imap, smtp, compacto = false }: { imap: EstadoCanal; smtp: EstadoCanal; compacto?: boolean },
) {
  return (
    <>
      <MailboxChannelBadge canal="imap" estado={imap} compacto={compacto} />
      <MailboxChannelBadge canal="smtp" estado={smtp} compacto={compacto} />
    </>
  );
}
